import useInterval from '@use-it/interval';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Subject } from 'rxjs';
import dynamic from 'next/dynamic';
import { Map, Polyline, GeoJSON, Marker, ScaleControl } from 'react-leaflet';
import { debounceTime } from 'rxjs/operators';
import { distanceTo } from 'geolocation-utils';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import LocationContext from '../../context/location-context';
import PostContext from '../../context/post-context';
import {
  fetchLocationsByBounds,
  fetchGpsJson,
  fetchAllPosts
} from '../../utils/fetchUtils';
import { default as europe } from '../../data/geojson/europe.json';
import './MapPanel.css';
import './tabs.css';
import MapLayers from './layers/MapLayers';
import MapMarkers from './markers/MapMarkers';
import { createClusterCustomIcon } from './markers/cluster-poi/ClusterPoi';
import InfoPanel from './infopanel/InfoPanel';
import {
  calculateTraversedDistance,
  generateMarkerPoints
} from '../../utils/utils';
import {
  startFinishIcon,
  currentPositionIcon,
  FULL_DISTANCE_KM
} from '../../utils/constants';
const MarkerClusterGroup = dynamic(import('react-leaflet-markercluster'), {
  ssr: false
});

const MapPanel = (props, state) => {
  const { locations, dispatchLocations } = useContext(LocationContext);
  const { posts, dispatchPosts } = useContext(PostContext);
  const [isGpsReq, setIsGpsReq] = useState(false);
  const [markers, setMarkers] = useState();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [images, setImages] = useState(0);
  const [isOpenImageBox, setIsOpenImageBox] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [points, setPoints] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [user] = useState({ value: 'prod', label: 'prod' });
  const [centerPosition, setCenterPosition] = useState([52.25597, 21.09379]);
  const [activeLayer, setActiveLayer] = useState(0);
  const [dist, setDistance] = useState(0);

  const refreshAction = () => {
    fetchLocationsByBounds(
      dispatchLocations,
      locations.lastFetchDate,
      mapRef.current.leafletElement.getZoom(),
      mapRef.current.leafletElement.getBounds(),
      user.value
    );
    if (!isGpsReq) {
      fetchGpsJson(dispatchLocations);
      setIsGpsReq(true);
    }
  };

  let lastRoutePoint = [52.2305, 21.00694];
  let startPoint = [52.2305, 21.00694];
  // let finishPoint = [22.95217, -16.15861];

  // const handleChange = selectedOption => {
  //   dispatchLocations({ type: 'CLEAR_LOCATIONS' });
  //   setUser(selectedOption);
  //   dispatchLocations({ type: 'FETCH_LOCATIONS', data: { items: [] } });
  //   setPoints([]);
  //   setMarkers([]);
  //   fetchLocationsByBounds(
  //     dispatchLocations,
  //     locations.lastFetchDate,
  //     mapRef.current.leafletElement.getZoom(),
  //     mapRef.current.leafletElement.getBounds(),
  //     selectedOption.value
  //   );
  // };

  if (points && points[0]) {
    startPoint = [points[0][0], points[0][1]];
  }
  if (points) {
    const length = points.length;
    const value = points[length - 1];
    if (value) {
      lastRoutePoint = [value[0], value[1]];
    }
  }
  const [zoom, setZoom] = useState(10);

  const mapRef = useRef(null);
  const onViewportChanged$ = new Subject();
  onViewportChanged$.pipe(debounceTime(750)).subscribe(evt => {
    locations.e_gen_ts = undefined; // workaround to force getting data from beginning
    if (mapRef && mapRef.current && mapRef.current.leafletElement) {
      fetchLocationsByBounds(
        dispatchLocations,
        null,
        mapRef.current.leafletElement.getZoom(),
        mapRef.current.leafletElement.getBounds(),
        user.value
      );
    }
  });
  onViewportChanged$.subscribe(evt => {
    setIsTouched(true);
  });

  const centerMap = () => {
    const length = locations.locations.length;
    const value = locations.locations[length - 1];
    if (value) {
      // Hack so the lat/long are always at least a little bit different.
      // Unfortunately setCenterPosition is not working with the same args in a row.
      setCenterPosition([
        value.e_pos_lat + Math.round(Math.random() * 100) / 10000000,
        value.e_pos_long
      ]);
      setIsTouched(false);
    }
  };

  useInterval(refreshAction, 20000);
  useEffect(refreshAction, []);
  useEffect(() => {
    if (locations && locations.locations && locations.locations.length > 0) {
      setPoints(
        locations.locations.map(point => [point.e_pos_lat, point.e_pos_long])
      );
      if (!isTouched && !isOpenImageBox && !isPopupOpen) centerMap();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  useEffect(() => {
    if (posts && posts.posts && posts.posts.length > 0) {
      generateMarkerPoints(
        posts.posts,
        setMarkers,
        setImages,
        setIsOpenImageBox,
        setIsPopupOpen
      );
    }
  }, [posts]);

  useInterval(() => {
    fetchAllPosts(dispatchPosts);
  }, 5 * 60 * 1000);
  useEffect(() => {
    fetchAllPosts(dispatchPosts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stages = locations && locations.gps ? locations.gps : [];

  let distance = 0;
  let minDistance = 0;
  let minIdx = 0;
  stages.forEach((item, idx) => {
    distance = distanceTo(item, lastRoutePoint);
    if (minDistance === 0) {
      minDistance = distance;
      minIdx = idx;
    } else if (distance < minDistance) {
      minDistance = distance;
      minIdx = idx;
    }
  });

  let stagesNotPassed = stages.slice(minIdx);
  let stagesPassed = stages.slice(0, minIdx);

  // Adding last point to connect passed route and planned route
  stagesNotPassed.unshift(lastRoutePoint);
  stagesPassed.push(lastRoutePoint);
  if (stagesPassed.length > 1) {
    const calcDist = calculateTraversedDistance(stagesPassed);
    if (dist !== calcDist) setDistance(calcDist);
  }

  return (
    <React.Fragment>
      <div className="map-wrapper">
        <div className="oracle-logo">
          <img src={require('../../assets/images/oracle.svg')} alt="" />
        </div>
        <Map
          key={centerPosition[0]}
          ref={mapRef}
          maxZoom="25"
          zoom={zoom}
          center={centerPosition}
          onZoomEnd={evt => setZoom(evt.sourceTarget.getZoom())}
          onViewportChanged={e => {
            onViewportChanged$.next(e);
          }}
          useFlyTo={true}
        >
          <MarkerClusterGroup
            iconCreateFunction={createClusterCustomIcon}
            polygonOptions={{
              fillColor: 'red',
              color: 'red',
              weight: 0.5,
              opacity: 0.7,
              fillOpacity: 0.2
            }}
          >
            {markers}
            <MapMarkers zoom={zoom}></MapMarkers>
          </MarkerClusterGroup>
          <span>
            {points && points.length > 0 && (
              <Marker
                position={points[points.length - 1]}
                icon={currentPositionIcon}
                zIndexOffset={1000}
              ></Marker>
            )}
            <Marker position={startPoint} icon={startFinishIcon}></Marker>
          </span>

          <Polyline
            color="#ec1d25"
            weight="4"
            opacity="0.2"
            positions={stagesNotPassed}
          />
          {points && points.length > 0 && (
            <Polyline
              color="#ec1d25"
              weight="3"
              opacity="1"
              positions={points}
            />
          )}
          <GeoJSON
            data={europe}
            style={{ color: '#ec1d25', fill: false, weight: 2 }}
          />
          <MapLayers
            activeLayer={activeLayer}
            setActiveLayer={setActiveLayer}
          />
          <ScaleControl position="bottomleft" />
        </Map>
      </div>
      {isOpenImageBox && (
        <Lightbox
          mainSrc={images[photoIndex]}
          nextSrc={images[(photoIndex + 1) % images.length]}
          prevSrc={images[(photoIndex + images.length - 1) % images.length]}
          onCloseRequest={() => {
            setIsOpenImageBox(false);
          }}
          onMovePrevRequest={() => {
            setPhotoIndex((photoIndex + images.length - 1) % images.length);
          }}
          onMoveNextRequest={() => {
            setPhotoIndex((photoIndex + 1) % images.length);
          }}
        />
      )}
      {isTouched && (
        <button
          className={'reset-btn gps-center'}
          title={
            'Manual operation enabled. Press here to go back to follow bikers mode.'
          }
          onClick={centerMap}
        ></button>
      )}
      <InfoPanel
        currentDist={dist / 1000}
        fullDist={FULL_DISTANCE_KM}
      ></InfoPanel>
    </React.Fragment>
  );
};

export { MapPanel as default };
